import React, { useState, useRef, useEffect } from "react";
import { DateRange } from "react-date-range";
import { format, subDays } from "date-fns";
import { enUS } from "date-fns/locale";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { useQueryParams } from "context/QueryParamsContext";
import { getDomainIdSync } from "utils/utils";
import axios from "axios";
import { API_BASE_URL } from "constants";
import { LAMBDA_BASE_URL } from "constants";
import { getDomainId } from "utils/utils";
import { useSystemTheme } from "context/SystemThemeContext";

function CustomdateD({ onRangeSelect = () => {} }) {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const [showPicker, setShowPicker] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);
  const [selectedLabel, setSelectedLabel] = useState("Last 30 Days");
  const { setDateRange } = useQueryParams();
  const [allTimeStartDate, setStartDate] = useState("");

  const [state, setState] = useState([
    {
      startDate: subDays(new Date(), 30),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const fetchUsers = async () => {
    try {
      const domainId = await getDomainId();
      const res = await axios.get(`${LAMBDA_BASE_URL}/total_searches`, {
        params: {
          limit: 10,
          sort_key: "created_at",
          sort_direction: "asc",
        },
        headers: { "DOMAIN-TOKEN": domainId },
      });

      const createdAt = new Date(res.data.result.search_list[0].created_at);
      const stdate = format(createdAt, "yyyy-MM-dd");
      setStartDate(stdate);
      return stdate;
    } catch (err) {
      console.error("Error fetching users", err);
      return null;
    }
  };

  useEffect(() => {
    const savedStart = localStorage.getItem("saved_start_date");
    const savedEnd = localStorage.getItem("saved_end_date");

    if (savedStart && savedEnd) {
      const startDate = new Date(savedStart);
      const endDate = new Date(savedEnd);

      setState([{ startDate, endDate, key: "selection" }]);

      const formattedStart = format(startDate, "MMM d, yyyy");
      const formattedEnd = format(endDate, "MMM d, yyyy");
      setSelectedLabel(`${formattedStart} – ${formattedEnd}`);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePresetSelect = async (label) => {
    setShowDropdown(false);
    setShowPicker(false);

    const today = new Date();
    let start = null;
    let end = null;

    switch (label) {
      case "Today":
        start = end = format(today, "yyyy-MM-dd");
        break;
      case "Yesterday":
        start = end = format(subDays(today, 1), "yyyy-MM-dd");
        break;
      case "Last 7 Days":
        start = format(subDays(today, 6), "yyyy-MM-dd");
        end = format(today, "yyyy-MM-dd");
        break;
      case "Last 30 Days":
        start = format(subDays(today, 30), "yyyy-MM-dd");
        end = format(today, "yyyy-MM-dd");
        break;
      case "All Data":
        if (localStorage.getItem("is_super_admin") === "true") {
          start = "2024-01-01";
          end = format(today, "yyyy-MM-dd");
        } else {
          const earliest = await fetchUsers();
          if (!earliest) return;
          start = earliest;
          end = format(today, "yyyy-MM-dd");
        }
        break;
      case "Custom":
        setSelectedLabel("Custom");
        setShowPicker(true);
        return;
      default:
        return;
    }

    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const labelStart = format(startDateObj, "MMM d, yyyy");
    const labelEnd = format(endDateObj, "MMM d, yyyy");

    setSelectedLabel(`${labelStart} – ${labelEnd}`);
    setState([{ startDate: startDateObj, endDate: endDateObj, key: "selection" }]);

    localStorage.setItem("saved_start_date", start);
    localStorage.setItem("saved_end_date", end);

    setDateRange({ start_date: start, end_date: end });
    onRangeSelect({ start, end });
  };

  const handleChange = (ranges) => {
    const { startDate, endDate } = ranges.selection;
    setState([ranges.selection]);

    if (!startDate || !endDate || startDate.getTime() === endDate.getTime()) {
      return;
    }

    const formattedStart = format(startDate, "yyyy-MM-dd");
    const formattedEnd = format(endDate, "yyyy-MM-dd");

    const labelStart = format(startDate, "MMM d, yyyy");
    const labelEnd = format(endDate, "MMM d, yyyy");

    setSelectedLabel(`${labelStart} – ${labelEnd}`);
    setShowPicker(false);

    localStorage.setItem("saved_start_date", formattedStart);
    localStorage.setItem("saved_end_date", formattedEnd);

    setDateRange({ start_date: formattedStart, end_date: formattedEnd });
    onRangeSelect({ start: formattedStart, end: formattedEnd });
  };

  // Dark mode styles for react-date-range
  useEffect(() => {
    if (isDarkMode) {
      const style = document.createElement('style');
      style.id = 'date-range-dark-styles';
      style.textContent = `
        .rdrCalendarWrapper {
          background-color: #060C18 !important;
          color: #e2e8f0 !important;
        }
        .rdrDateDisplayWrapper {
          background-color: #060C18 !important;
        }
        .rdrDateDisplay {
          background-color: #060C18 !important;
        }
        .rdrDateDisplayItem {
          background-color: #2d2d3f !important;
          border-color: #4a5568 !important;
          color: #e2e8f0 !important;
        }
        .rdrDateDisplayItem input {
          color: #e2e8f0 !important;
        }
        .rdrMonthAndYearWrapper {
          background-color: #060C18 !important;
          color: #e2e8f0 !important;
        }
        .rdrMonthAndYearPickers select {
          background-color: #2d2d3f !important;
          color: #e2e8f0 !important;
          border-color: #4a5568 !important;
        }
        .rdrMonthPicker select, .rdrYearPicker select {
          background-color: #2d2d3f !important;
          color: #e2e8f0 !important;
        }
        .rdrWeekDay {
          color: #a0aec0 !important;
        }
        .rdrDayNumber span {
          color: #e2e8f0 !important;
        }
        .rdrDayPassive .rdrDayNumber span {
          color: #a0aec0 !important;
        }
        .rdrDayToday .rdrDayNumber span:after {
          background: #219297 !important;
        }
        .rdrDayHovered .rdrDayNumber:after {
          background-color: #2d2d3f !important;
        }
        .rdrDayStartPreview, .rdrDayInPreview, .rdrDayEndPreview {
          background-color: #2d2d3f !important;
          border-color: #4a5568 !important;
        }
        .rdrDayStartOfMonth .rdrDayInPreview, .rdrDayStartOfWeek .rdrDayInPreview {
          background-color: #2d2d3f !important;
        }
        .rdrDay:hover .rdrDayNumber:after {
          background-color: #2d2d3f !important;
        }
        .rdrDefinedRangesWrapper {
          background-color: #060C18 !important;
          border-right-color: #2d2d3f !important;
        }
        .rdrStaticRange {
          background-color: #060C18 !important;
          color: #e2e8f0 !important;
        }
        .rdrStaticRange:hover {
          background-color: #2d2d3f !important;
        }
        .rdrStaticRangeLabel {
          color: #e2e8f0 !important;
        }
        .rdrInputRange {
          background-color: #060C18 !important;
        }
        .rdrInputRangeInput {
          background-color: #2d2d3f !important;
          border-color: #4a5568 !important;
          color: #e2e8f0 !important;
        }
        .rdrDateRangePickerWrapper {
          background-color: #060C18 !important;
        }
      `;
      
      if (!document.getElementById('date-range-dark-styles')) {
        document.head.appendChild(style);
      }
      
      return () => {
        const existingStyle = document.getElementById('date-range-dark-styles');
        if (existingStyle) existingStyle.remove();
      };
    }
  }, [isDarkMode]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        display: "flex",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        width: "100%",
      }}
    >
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        style={{
          padding: "10px 12px",
          borderRadius: "6px",
          background: "#219297",
          color: "white",
          border: "none",
          cursor: "pointer",
          fontSize: "14px",
          maxWidth: "100%",
          boxShadow:
            "-2px 0 4px rgba(0,0,0,0.05), 2px 0 4px rgba(0,0,0,0.05), 0 2px 6px rgba(0,0,0,0.1)",
          fontWeight: "600",
          transition: "filter 0.3s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.1)")}
        onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
      >
        {selectedLabel}
      </button>

      {showDropdown && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            background: isDarkMode ? "#060C18" : "#fff",
            boxShadow: isDarkMode 
              ? "0 0 10px rgba(0,0,0,0.3)" 
              : "0 0 10px rgba(0,0,0,0.15)",
            zIndex: 1000,
            borderRadius: "6px",
            padding: "8px 0",
            minWidth: "160px",
            maxWidth: "200px",
            border: isDarkMode ? "1px solid #2d2d3f" : "none",
          }}
        >
          {["Today", "Yesterday", "Last 7 Days", "Last 30 Days", "Custom", "All Data"].map(
            (label) => (
              <div
                key={label}
                onClick={() => handlePresetSelect(label)}
                style={{
                  padding: "8px 16px",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: isDarkMode ? "#e2e8f0" : "#333",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = isDarkMode ? "#2d2d3f" : "#f0f0f0";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                }}
              >
                {label}
              </div>
            )
          )}
        </div>
      )}

      {showPicker && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: "5px",
            zIndex: 1000,
            boxShadow: isDarkMode 
              ? "0 0 10px rgba(0,0,0,0.3)" 
              : "0 0 10px rgba(0,0,0,0.1)",
            borderRadius: "8px",
            background: isDarkMode ? "#060C18" : "#fff",
            padding: "0px",
            maxWidth: "100vw",
            border: isDarkMode ? "1px solid #2d2d3f" : "none",
          }}
        >
          <div style={{ 
            padding: "8px 12px", 
            fontSize: "13px", 
            color: isDarkMode ? "#a0aec0" : "#666",
            borderBottom: isDarkMode ? "1px solid #2d2d3f" : "none",
          }}>
            Select a start and end date.
          </div>
          <DateRange
            editableDateInputs={true}
            onChange={handleChange}
            moveRangeOnFirstSelection={false} 
            ranges={state}
            locale={enUS}
            maxDate={new Date()}
            months={1}
          />
        </div>
      )}
    </div>
  );
}

export default CustomdateD;